import moment from "moment";
import React, { useMemo, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { BtnComponent } from "../../components/button";
import { PageDialogContent, PageDialogFooter } from "../../components/dialog";
import { useGetChatGroupLocationsQuery } from "../../services/appointmentService";

function checkInStatusLabel(item) {
  if (!item.checked_in) return "Checked Out";
  if (item.checked_in_time && moment(item.checked_in_time).isValid()) {
    return `Checked in since ${moment(item.checked_in_time).format("MM/DD/YYYY hh:mm A")}`;
  }
  return "Checked In";
}

const CheckedInLocationItem = ({ item }) => {
  if (item.is_group) {
    return (
      <div className="border-b-[0.5px] py-3 px-3 min-w-0">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex w-full min-w-0 justify-center">
            <div className="inline-flex max-w-full min-w-0 items-center gap-2">
              <UserGroupIcon
                className="block h-5 w-5 shrink-0 text-emerald-700"
                aria-hidden
              />
              <span className="min-w-0 text-center text-sm font-medium leading-snug text-black break-words">
                {item.name}
              </span>
            </div>
          </div>
          <div className="break-words text-center text-xs text-gray-600">
            {(item.locations || [])
              .map((loc) => loc?.name)
              .filter(Boolean)
              .join(", ") || "No locations"}
          </div>
          <div className="break-words text-center text-xs text-gray-600">
            {checkInStatusLabel(item)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b-[0.5px] py-3 px-3 min-w-0">
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="text-sm font-medium text-black break-words">{item.name}</div>
        <div className="text-xs text-gray-600 break-words">{checkInStatusLabel(item)}</div>
      </div>
    </div>
  );
};

const ProviderCheckedInLocations = ({
  closeLocationApptPopup = () => {},
  currentUser = null,
}) => {
  const [search, setSearch] = useState("");

  const { data: chatLocation, isFetching } = useGetChatGroupLocationsQuery(
    currentUser?.user_id
      ? { user_id: currentUser.user_id, include_groups: 1 }
      : { include_groups: 1 },
    { refetchOnMountOrArgChange: true }
  );

  const checkedInLocations = useMemo(
    () => (chatLocation || []).filter((item) => item.checked_in),
    [chatLocation]
  );

  const filteredCheckedInLocations = useMemo(
    () =>
      checkedInLocations.filter((item) =>
        item.name?.toLowerCase().includes(search.toLowerCase())
      ),
    [checkedInLocations, search]
  );

  return (
    <>
      <PageDialogContent
        contentCls="pt-2 px-4 min-h-0"
        hideOverFlow={true}
        contentScroll={false}
      >
        <div className="w-full text-gray-500 min-h-32 relative">
          {isFetching ? (
            <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto absolute inset-0 outline-none focus:outline-none before:absolute before:flex before:w-full before:h-full z-[999]">
              <FaSpinner
                className="animate-spin text-green-300-500 ml-1"
                size={40}
              />
            </div>
          ) : null}

          <div className="flex flex-col w-full border rounded-lg bg-white min-h-[320px] max-h-[55vh]">
            <div className="bg-green-50 px-3 sm:px-4 py-3 border-b rounded-t-lg flex-shrink-0">
              <h3 className="text-sm font-semibold text-green-700 mb-2">
                Checked In Locations ({filteredCheckedInLocations.length})
              </h3>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search checked in..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
                aria-label="Search checked in locations"
              />
            </div>
            <div className="overflow-y-auto flex-1 min-h-0">
              {filteredCheckedInLocations.length > 0 ? (
                filteredCheckedInLocations.map((item) => (
                  <CheckedInLocationItem key={item.id} item={item} />
                ))
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  <p className="text-sm">
                    {search
                      ? "No matching checked in locations"
                      : "No checked in locations"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageDialogContent>
      <PageDialogFooter>
        <BtnComponent
          text="Close"
          btnType="default"
          onClick={closeLocationApptPopup}
        />
      </PageDialogFooter>
    </>
  );
};

export default ProviderCheckedInLocations;
